// Import hooks.
import {
    UseInputAcknowledgementCheckboxResults,
    useInputAcknowledgementCheckbox,
    useInputDropdown,
    UseInputDropdownResults,
    UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
    FormProducerSignupProps,
} from '@app/props';
// Import api models.
import {
    PossibleHeardThroughOption,
} from '@app/models/api';
import DropdownItem from 'Models/AppModels/DropdownItem';

interface UseSubformProducerAgreementParameters {
    id: string;
    showValidations?: boolean;
    props: FormProducerSignupProps;
}

interface UseSubformProducerAgreementResults {
    heardThroughOptionInput: UseInputDropdownResults;
    acknowledgementInput: UseInputAcknowledgementCheckboxResults;
    flagAllInputsToShowValidations: () => void;
    checkFormValidationWithFocus: () => boolean;
}

const useSubformProducerAgreement = ( params: UseSubformProducerAgreementParameters ): UseSubformProducerAgreementResults => {
    
	// #region exported methods

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
        acknowledgementInput.changeShowValidationFlag(true);
        heardThroughOptionsInput.changeShowValidationFlag(true);
    };

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidationWithFocus = (): boolean => {
        return (
			checkInputValidationWithFocus<boolean>(acknowledgementInput)
            && checkInputValidationWithFocus<string>(heardThroughOptionsInput)
        );
    };

    // #endregion exported methods

    // #region private methods

    /**
     * Validates an individual input with focus.
     * @param input
     */
    const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
        let isValid = input.isInputValid;
        if (!isValid) {
            input.focusToInput();
        }
        return isValid;
    };

    //#region id generators

    const generateIdForForm = (): string => {
        return params.id;
    };

    const generateIdForHeardThroughOptionsInput = (): string => {
        return `${generateIdForForm()}-heardThroughOptions`;
    };

    const generateIdForAcknowledgementInput = (): string => {
        return `${generateIdForForm()}-acknowledgement`;
    };

    //#endregion id generators

    const getDefaultHeardThroughOptionDropdownItem = (): DropdownItem => {
        return new DropdownItem({
            label: '--Please select an option--',
            value: '0',
        });
    };

    const getHeardThroughOptionItems = (): DropdownItem[] => {
        return params.props.possibleHeardThroughOptions.map(
            (possibleHeardThroughOption: PossibleHeardThroughOption) => {
                return new DropdownItem({
                    label: possibleHeardThroughOption.displayName !== null ? possibleHeardThroughOption.displayName : String.empty(),
                    value: possibleHeardThroughOption.id !== null ? possibleHeardThroughOption.id.toString() : '0',
                });
            }
        );
    };

    // #endregion private methods

    // #region variables

    const heardThroughOptionsInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForHeardThroughOptionsInput(),
        label: 'How did you hear about us?',
        required: true,
        disabled: false,
        value: String.empty(),
        defaultDropdownItem: getDefaultHeardThroughOptionDropdownItem(),
        dropdownItems: getHeardThroughOptionItems(),
        showValidation: params.showValidations,
    });

    const acknowledgementInput: UseInputAcknowledgementCheckboxResults = useInputAcknowledgementCheckbox({
        id: generateIdForAcknowledgementInput(),
        label: 'I have read and acknowledge the CATcoverage producer agreement.',
        validationLabel: 'Please, read and acknowledge the CATCoverage producer agreement to get appointed.',
        required: true,
        disabled: false,
        isChecked: false,
        showValidation: params.showValidations,
    })
    // #endregion variables

    return {
        heardThroughOptionInput: heardThroughOptionsInput,
        acknowledgementInput: acknowledgementInput,
        flagAllInputsToShowValidations: flagAllInputsToShowValidations,
        checkFormValidationWithFocus: checkFormValidationWithFocus,
    };
};

// Export parameters.
export { UseSubformProducerAgreementParameters };

// Export results.
export { UseSubformProducerAgreementResults };

export default useSubformProducerAgreement;
